import React from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { COLORES_POR_DEFECTO } from '../../lib/branding';

/**
 * Elige los colores del negocio, al lado de la página de verdad.
 *
 * Reemplaza al modal "Cambiar colores" (components/BrandPreview.jsx) dentro del
 * onboarding. Ese modal traía su propia vista previa, una maqueta escrita
 * aparte que ya no se parecía a la página real: decía "Paso 1: Elige tu
 * Servicio" cuando la página dice "Elegí el servicio", y mostraba un encabezado
 * con un botón "Gestionar Turno" que no existe más. Elegir colores mirando una
 * pantalla que no es la tuya no sirve de nada.
 *
 * Acá no hace falta ninguna vista previa propia: el paso ya muestra la página
 * real debajo, y como esto escribe el color en el mismo estado, se actualiza
 * sola al tocar una paleta.
 *
 * (El modal sigue existiendo porque lo usa el panel. Unificarlo es parte de la
 * paridad panel ↔ onboarding.)
 */

/**
 * Los colores que se ofrecen, dando la vuelta al círculo cromático.
 *
 * El naranja de Aturno va primero para que volver al original sea elegir uno
 * más y no buscar un botón de "restaurar".
 *
 * Cada uno trae tres tonos porque la página usa los tres: el principal en los
 * botones, el secundario en el degradado del encabezado y el de acento en los
 * fondos suaves. El secundario es siempre una versión más oscura del principal
 * y el acento una más clara: así el degradado del encabezado se lee como una
 * sola familia y no como dos colores peleados.
 *
 * Todos son lo bastante oscuros como para que el texto blanco encima se lea;
 * `necesitaTextoBlanco` en lib/branding.js es el que decide, y con amarillos o
 * celestes pálidos devolvería texto negro y el encabezado quedaría lavado.
 */
const PALETAS = [
  { nombre: 'Naranja Aturno', ...COLORES_POR_DEFECTO },
  { nombre: 'Rojo', primaryColor: '#E53935', secondaryColor: '#B71C1C', accentColor: '#EF9A9A' },
  { nombre: 'Coral', primaryColor: '#FF7043', secondaryColor: '#E64A19', accentColor: '#FFAB91' },
  { nombre: 'Ámbar', primaryColor: '#FF9800', secondaryColor: '#F57C00', accentColor: '#FFB74D' },
  { nombre: 'Mostaza', primaryColor: '#C99700', secondaryColor: '#8D6E00', accentColor: '#F0C419' },
  { nombre: 'Oliva', primaryColor: '#7CB342', secondaryColor: '#558B2F', accentColor: '#AED581' },
  { nombre: 'Verde', primaryColor: '#4CAF50', secondaryColor: '#388E3C', accentColor: '#81C784' },
  { nombre: 'Esmeralda', primaryColor: '#00897B', secondaryColor: '#00695C', accentColor: '#4DB6AC' },
  { nombre: 'Turquesa', primaryColor: '#00ACC1', secondaryColor: '#00838F', accentColor: '#4DD0E1' },
  { nombre: 'Celeste', primaryColor: '#039BE5', secondaryColor: '#0277BD', accentColor: '#4FC3F7' },
  { nombre: 'Azul', primaryColor: '#1976D2', secondaryColor: '#0D47A1', accentColor: '#64B5F6' },
  { nombre: 'Índigo', primaryColor: '#3949AB', secondaryColor: '#283593', accentColor: '#7986CB' },
  { nombre: 'Violeta', primaryColor: '#8E24AA', secondaryColor: '#6A1B9A', accentColor: '#BA68C8' },
  { nombre: 'Fucsia', primaryColor: '#D81B60', secondaryColor: '#AD1457', accentColor: '#F06292' },
  { nombre: 'Rosa', primaryColor: '#EC407A', secondaryColor: '#C2185B', accentColor: '#F8BBD0' },
  { nombre: 'Chocolate', primaryColor: '#6D4C41', secondaryColor: '#4E342E', accentColor: '#A1887F' },
  { nombre: 'Grafito', primaryColor: '#455A64', secondaryColor: '#263238', accentColor: '#90A4AE' },
  { nombre: 'Negro', primaryColor: '#212121', secondaryColor: '#000000', accentColor: '#616161' },
];

export default function SelectorDeColores({ value, onChange }) {
  const actual = (value?.primaryColor || COLORES_POR_DEFECTO.primaryColor).toUpperCase();
  const elegida = PALETAS.find((p) => p.primaryColor.toUpperCase() === actual);

  const elegir = (paleta) =>
    onChange({
      ...value,
      primaryColor: paleta.primaryColor,
      secondaryColor: paleta.secondaryColor,
      accentColor: paleta.accentColor,
    });

  return (
    <div className="selector-colores">
      <div className="selector-colores-paletas" role="group" aria-label="Colores del negocio">
        {PALETAS.map((paleta) => {
          const esta = paleta.primaryColor.toUpperCase() === actual;
          return (
            /* Solo el círculo, sin el nombre al lado. Con siete colores las
               pastillas con nombre entraban en dos renglones; con dieciocho
               serían cinco, más altas que la vista previa que está justo debajo
               y que es lo que en realidad hay que mirar para elegir. El nombre
               vive en el aria-label, en el title y —para el elegido— en la línea
               de abajo. */
            <button
              key={paleta.nombre}
              type="button"
              onClick={() => elegir(paleta)}
              aria-pressed={esta}
              aria-label={paleta.nombre}
              title={paleta.nombre}
              className={cn("selector-colores-paleta", esta && "elegida")}
              style={{ '--muestra': paleta.primaryColor }}
            >
              <span className="selector-colores-muestra" aria-hidden="true">
                {esta && <Check className="h-4 w-4" strokeWidth={3} />}
              </span>
            </button>
          );
        })}
      </div>

      {/* El color libre queda al final y no como una pantalla aparte: es la
          excepción, no el camino principal. */}
      <label className="selector-colores-propio">
        <input
          type="color"
          value={actual}
          onChange={(e) =>
            onChange({
              ...value,
              primaryColor: e.target.value,
              // El secundario y el de acento acompañan al principal si venían de
              // una paleta; si no, el degradado del encabezado quedaría con el
              // color viejo y no se entendería qué cambió.
              secondaryColor: e.target.value,
              accentColor: e.target.value,
            })
          }
        />
        <span>
          Otro color
          {/* El nombre del color elegido: es lo que se perdió al dejar los
              círculos sin texto. Si el color no es de la lista —salió de acá
              mismo— solo va el hexadecimal. */}
          <span className="selector-colores-hex">
            {elegida ? `${elegida.nombre} · ${actual}` : actual}
          </span>
        </span>
      </label>
    </div>
  );
}
